// frontend/src/pages/UserManagement.js
import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import axios from '../utils/axios';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Grid,
  Avatar,
  Typography,
  IconButton,
  Chip,
  InputAdornment,
  Paper,
  Stack,
  CircularProgress
} from '@mui/material';
import { 
  Add as AddIcon, 
  Edit as EditIcon, 
  Delete as DeleteIcon,
  Person as PersonIcon,
  Shield as ShieldIcon,
  Search as SearchIcon,
  Visibility as VisibilityIcon,
  FilterList as FilterListIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import SimpleDataTable from '../components/ui/SimpleDataTable';

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogType, setDialogType] = useState('add');
  const [selectedUser, setSelectedUser] = useState(null);
  
  // Filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [kycFilter, setKycFilter] = useState('all');
  const [roleFilter, setRoleFilter] = useState('all');
  
  // Pagination
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [pagination, setPagination] = useState(null);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    mobileNumber: '',
    password: '',
    isAdmin: false,
  });

  useEffect(() => {
    fetchUsers();
  }, [searchTerm, statusFilter, typeFilter, kycFilter, roleFilter, page]);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await axios.get('/admin/user-management/stats');
      setStats(response.data.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        search: searchTerm,
        status: statusFilter,
        type: typeFilter,
        kycStatus: kycFilter,
        role: roleFilter,
        page: page.toString(),
        limit: limit.toString()
      });

      const response = await axios.get(`/admin/user-management?${params}`);
      setUsers(response.data.data.users);
      setPagination(response.data.data.pagination);
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Failed to fetch users');
    } finally {
      setLoading(false);
    }
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setPage(1);
  };

  const handleFilterChange = (filterType, value) => {
    setPage(1);
    switch(filterType) {
      case 'status':
        setStatusFilter(value);
        break;
      case 'type':
        setTypeFilter(value);
        break;
      case 'kyc':
        setKycFilter(value);
        break;
      case 'role':
        setRoleFilter(value);
        break;
      default:
        break;
    }
  };

  const clearFilters = () => {
    setSearchTerm('');
    setStatusFilter('all');
    setTypeFilter('all');
    setKycFilter('all');
    setRoleFilter('all');
    setPage(1);
  };

  const handleOpenDialog = (type, user = null) => {
    setDialogType(type);
    setSelectedUser(user);
    if ((type === 'edit' || type === 'view') && user) {
      // FIXED: Load actual user data for view/edit
      setFormData({
        name: user.originalName || user.name,
        email: user.email || '',
        mobileNumber: user.mobileNumber || '',
        password: '',
        isAdmin: user.role === 'SYSTEM_ADMIN',
      });
    } else {
      setFormData({
        name: '',
        email: '',
        mobileNumber: '',
        password: '',
        isAdmin: false,
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedUser(null);
    setFormData({
      name: '',
      email: '',
      mobileNumber: '',
      password: '',
      isAdmin: false,
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async () => {
    try {
      if (!formData.email && !formData.mobileNumber) {
        toast.error('Please provide either email or mobile number');
        return;
      }

      if (dialogType === 'add') {
        await axios.post('/users', formData);
        toast.success('User added successfully');
      } else {
        await axios.put(`/users/${selectedUser._id}`, formData);
        toast.success('User updated successfully');
      }
      handleCloseDialog();
      fetchUsers();
      fetchStats();
    } catch (error) {
      console.error('Error saving user:', error);
      toast.error(error.response?.data?.message || 'Failed to save user');
    }
  };

  const handleDelete = async (userId) => {
    if (window.confirm('Are you sure you want to delete this user? This action will soft delete the user.')) {
      try {
        await axios.delete(`/admin/user-management/${userId}`, {
          data: { reason: 'Deleted by admin' }
        });
        toast.success('User deleted successfully');
        fetchUsers();
        fetchStats();
      } catch (error) {
        console.error('Error deleting user:', error);
        toast.error('Failed to delete user');
      }
    }
  };

  const getInitials = (name) => {
    return name
      ?.split(' ')
      .map(word => word[0])
      .join('')
      .toUpperCase()
      .slice(0, 2) || 'U';
  };

  const getKycStatusChip = (kyc) => {
    const statusConfig = {
      verified: { label: 'Verified', color: 'success' },
      pending: { label: 'Pending', color: 'warning' },
      rejected: { label: 'Rejected', color: 'error' },
      not_started: { label: 'Not Started', color: 'default' }
    };
    
    const config = statusConfig[kyc?.status] || statusConfig.not_started;
    
    return (
      <Chip
        label={config.label}
        color={config.color}
        size="small"
        variant="outlined"
      />
    );
  };

  // FIXED: Status chip - only Active and Deleted
  const getStatusChip = (status) => {
    const statusConfig = {
      active: { label: 'Active', color: 'success' },
      deleted: { label: 'Deleted', color: 'error' }
    };
    
    const config = statusConfig[status] || statusConfig.active;
    
    return (
      <Chip
        label={config.label}
        color={config.color}
        size="small"
        variant="filled"
      />
    );
  };

  const getTypeChip = (type) => {
    if (type === 'Free' || !type) {
      return (
        <Chip
          label="Free"
          color="default"
          size="small"
          variant="outlined"
        />
      );
    }
    
    return (
      <Chip
        label="Premium"
        color="secondary"
        size="small"
        variant="filled"
      />
    );
  };

  const columns = [
    {
      id: 'user',
      label: 'USER',
      render: (value, row) => (
        <Box display="flex" alignItems="center" gap={2}>
          <Avatar 
            sx={{ 
              bgcolor: 'primary.main', 
              width: 40, 
              height: 40,
              fontWeight: 600
            }}
          >
            {getInitials(row.name)}
          </Avatar>
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
              {row.name}
            </Typography>
            {/* FIXED: Show email with priority, else show mobile */}
            <Typography variant="caption" color="textSecondary">
              {row.email || row.mobileNumber || 'No contact info'}
            </Typography>
          </Box>
        </Box>
      )
    },
    {
      id: 'role',
      label: 'ROLE',
      render: (value, row) => (
        <Chip
          label={row.role === 'SYSTEM_ADMIN' ? 'Admin' : 'User'}
          color={row.role === 'SYSTEM_ADMIN' ? 'primary' : 'default'}
          size="small"
          icon={row.role === 'SYSTEM_ADMIN' ? <ShieldIcon fontSize="small" /> : <PersonIcon fontSize="small" />}
        />
      )
    },
    {
      id: 'type',
      label: 'TYPE',
      render: (value, row) => getTypeChip(row.type)
    },
    {
      id: 'status',
      label: 'STATUS',
      render: (value, row) => getStatusChip(row.status)
    },
    {
      id: 'kyc',
      label: 'KYC',
      render: (value, row) => getKycStatusChip(row.kyc)
    },
    {
      id: 'joined',
      label: 'JOINED',
      render: (value, row) => (
        <Typography variant="body2">
          {new Date(row.joinedDate).toLocaleDateString('en-US', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit'
          })}
        </Typography>
      )
    },
    {
      id: 'actions',
      label: 'ACTIONS',
      align: 'center',
      render: (value, row) => (
        <Box display="flex" gap={1} justifyContent="center">
          <IconButton
            size="small"
            color="primary"
            onClick={() => handleOpenDialog('view', row)}
            title="View Details"
          >
            <VisibilityIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            color="primary"
            onClick={() => handleOpenDialog('edit', row)}
            title="Edit User"
          >
            <EditIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            color="error"
            onClick={() => handleDelete(row._id)}
            title="Delete User"
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Box>
      )
    }
  ];

  const activeFiltersCount = [
    statusFilter !== 'all',
    typeFilter !== 'all',
    kycFilter !== 'all',
    roleFilter !== 'all',
    searchTerm !== ''
  ].filter(Boolean).length;

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" fontWeight="bold" gutterBottom>
            User Management
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Manage all user accounts and profiles
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog('add')}
          sx={{ borderRadius: 2 }}
        >
          Add User
        </Button>
      </Box>

      {/* Stats Cards */}
      {stats && (
        <Grid container spacing={2} mb={3}>
          <Grid item xs={12} sm={6} md={3}>
            <Paper sx={{ p: 2, borderRadius: 2 }}>
              <Typography variant="caption" color="textSecondary">Total Users</Typography>
              <Typography variant="h5" fontWeight="bold">{stats.users.totalUsers}</Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Paper sx={{ p: 2, borderRadius: 2 }}>
              <Typography variant="caption" color="textSecondary">Active Users</Typography>
              <Typography variant="h5" fontWeight="bold" color="success.main">
                {stats.users.activeUsers}
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Paper sx={{ p: 2, borderRadius: 2 }}>
              <Typography variant="caption" color="textSecondary">KYC Verified</Typography>
              <Typography variant="h5" fontWeight="bold" color="primary.main">
                {stats.kyc.verified}
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Paper sx={{ p: 2, borderRadius: 2 }}>
              <Typography variant="caption" color="textSecondary">Premium Users</Typography>
              <Typography variant="h5" fontWeight="bold" color="secondary.main">
                {stats.subscriptions.premium}
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      )}

      {/* Search and Filters */}
      <Paper sx={{ p: 2, mb: 3, borderRadius: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              placeholder="Search users..."
              value={searchTerm}
              onChange={handleSearchChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
                endAdornment: searchTerm && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setSearchTerm('')}>
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                )
              }}
              size="small"
            />
          </Grid>

          <Grid item xs={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>All Status</InputLabel>
              <Select
                value={statusFilter}
                label="All Status"
                onChange={(e) => handleFilterChange('status', e.target.value)}
              >
                <MenuItem value="all">All Status</MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="deleted">Deleted</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>All Types</InputLabel>
              <Select
                value={typeFilter}
                label="All Types"
                onChange={(e) => handleFilterChange('type', e.target.value)}
              >
                <MenuItem value="all">All Types</MenuItem>
                <MenuItem value="free">Free</MenuItem>
                <MenuItem value="premium">Premium</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>KYC Status</InputLabel>
              <Select
                value={kycFilter}
                label="KYC Status"
                onChange={(e) => handleFilterChange('kyc', e.target.value)}
              >
                <MenuItem value="all">All KYC</MenuItem>
                <MenuItem value="verified">Verified</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="rejected">Rejected</MenuItem>
                <MenuItem value="not_started">Not Started</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Role</InputLabel>
              <Select
                value={roleFilter}
                label="Role"
                onChange={(e) => handleFilterChange('role', e.target.value)}
              >
                <MenuItem value="all">All Roles</MenuItem>
                <MenuItem value="USER">User</MenuItem>
                <MenuItem value="SYSTEM_ADMIN">Admin</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        {activeFiltersCount > 0 && (
          <Box mt={2} display="flex" alignItems="center" gap={1}>
            <FilterListIcon fontSize="small" color="action" />
            <Typography variant="caption" color="textSecondary">
              {activeFiltersCount} filter{activeFiltersCount > 1 ? 's' : ''} active
            </Typography>
            <Button
              size="small"
              onClick={clearFilters}
              startIcon={<CloseIcon fontSize="small" />}
            >
              Clear All
            </Button>
          </Box>
        )}
      </Paper>

      {/* Data Table */}
      <Paper sx={{ borderRadius: 2, overflow: 'hidden' }}>
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" p={8}>
            <CircularProgress />
          </Box>
        ) : (
          <SimpleDataTable
            columns={columns}
            rows={users}
            loading={loading}
          />
        )}

        {/* Pagination Info - FIXED */}
        {pagination && (
          <Box p={2} display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="body2" color="textSecondary">
              {/* FIXED: Show correct range */}
              Showing {pagination.showing.from}-{pagination.showing.to} of {pagination.totalUsers} users
            </Typography>
            <Stack direction="row" spacing={1}>
              <Button
                size="small"
                disabled={page === 1}
                onClick={() => setPage(p => p - 1)}
              >
                Previous
              </Button>
              <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', px: 2 }}>
                Page {page} of {pagination.totalPages}
              </Typography>
              <Button
                size="small"
                disabled={page >= pagination.totalPages}
                onClick={() => setPage(p => p + 1)}
              >
                Next
              </Button>
            </Stack>
          </Box>
        )}
      </Paper>

      {/* Add/Edit/View Dialog */}
      <Dialog 
        open={openDialog} 
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2 }
        }}
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <PersonIcon color="primary" />
            {dialogType === 'add' ? 'Add New User' : dialogType === 'edit' ? 'Edit User' : 'View User'}
          </Box>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                autoFocus
                name="name"
                label="Full Name"
                fullWidth
                value={formData.name}
                onChange={handleInputChange}
                variant="outlined"
                disabled={dialogType === 'view'}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                name="email"
                label="Email Address"
                type="email"
                fullWidth
                value={formData.email}
                onChange={handleInputChange}
                variant="outlined"
                helperText="Email is optional if mobile number is provided"
                disabled={dialogType === 'view'}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                name="mobileNumber"
                label="Mobile Number"
                type="tel"
                fullWidth
                value={formData.mobileNumber}
                onChange={handleInputChange}
                variant="outlined"
                placeholder="+1234567890"
                helperText="Mobile number is optional if email is provided"
                disabled={dialogType === 'view'}
              />
            </Grid>
            {dialogType !== 'view' && (
              <Grid item xs={12}>
                <TextField
                  name="password"
                  label={dialogType === 'add' ? 'Password' : 'New Password (leave blank to keep current)'}
                  type="password"
                  fullWidth
                  value={formData.password}
                  onChange={handleInputChange}
                  variant="outlined"
                />
              </Grid>
            )}
            <Grid item xs={12}>
              <FormControl fullWidth variant="outlined">
                <InputLabel>Role</InputLabel>
                <Select
                  name="isAdmin"
                  value={formData.isAdmin}
                  onChange={(e) => setFormData({...formData, isAdmin: e.target.value})}
                  label="Role"
                  disabled={dialogType === 'view'}
                >
                  <MenuItem value={false}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <PersonIcon fontSize="small" />
                      End User
                    </Box>
                  </MenuItem>
                  <MenuItem value={true}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <ShieldIcon fontSize="small" />
                      Admin
                    </Box>
                  </MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={handleCloseDialog} variant="outlined">
            {dialogType === 'view' ? 'Close' : 'Cancel'}
          </Button>
          {dialogType !== 'view' && (
            <Button 
              onClick={handleSubmit} 
              variant="contained"
              startIcon={dialogType === 'add' ? <AddIcon /> : <EditIcon />}
            >
              {dialogType === 'add' ? 'Add User' : 'Update User'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UserManagement;